import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Search, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MembershipManagement({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const toggleMembership = async (userId, currentStatus) => {
    setUpdating(userId);
    try {
      const response = await axios.put(`${API}/users/${userId}/membership`, {
        membership_active: !currentStatus
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, membership_active: response.data.membership_active } : u));
      toast.success(!currentStatus ? 'Membership activated' : 'Membership deactivated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update membership');
    } finally {
      setUpdating(null);
    }
  };

  const filteredUsers = users.filter(u =>
    u.id !== user.id &&
    u.role !== 'head_coach' &&
    (u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const activeCount = filteredUsers.filter(u => u.membership_active !== false).length;
  const inactiveCount = filteredUsers.filter(u => u.membership_active === false).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="membership-management">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2 flex items-center gap-4">
            <Shield className="w-12 h-12 md:w-16 md:h-16 text-[#CCFF00]" />
            Membership
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">Manage member payment status and app access</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="total-members-stat">
            <p className="text-sm text-[#A1A1AA] uppercase tracking-wide mb-1">Total Members</p>
            <p className="text-3xl font-heading font-black text-[#FAFAFA]">{filteredUsers.length}</p>
          </Card>
          <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="active-members-stat">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-[#22C55E]" />
              <p className="text-sm text-[#A1A1AA] uppercase tracking-wide">Active</p>
            </div>
            <p className="text-3xl font-heading font-black text-[#22C55E]">{activeCount}</p>
          </Card>
          <Card className="bg-[#18181B] border-[#27272A] p-5" data-testid="inactive-members-stat">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-4 h-4 text-[#EF4444]" />
              <p className="text-sm text-[#A1A1AA] uppercase tracking-wide">Inactive</p>
            </div>
            <p className="text-3xl font-heading font-black text-[#EF4444]">{inactiveCount}</p>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B]" />
          <Input
            placeholder="Search members..."
            data-testid="search-members"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] h-12"
          />
        </div>

        {/* Members List */}
        <div className="space-y-3">
          {filteredUsers.map((member) => {
            const isActive = member.membership_active !== false;
            const isUpdating = updating === member.id;

            return (
              <Card
                key={member.id}
                className={`bg-[#18181B] border p-5 transition-colors ${
                  isActive ? 'border-[#27272A]' : 'border-[#EF4444]/30 bg-[#EF4444]/5'
                }`}
                data-testid={`member-card-${member.id}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isActive ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-heading font-bold uppercase text-[#FAFAFA] truncate">{member.name}</h3>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-sm uppercase flex-shrink-0 ${
                          member.role === 'head_coach' ? 'bg-[#CCFF00] text-black' :
                          member.role === 'coach' ? 'bg-[#2563EB] text-white' : 'bg-[#27272A] text-[#FAFAFA]'
                        }`}>
                          {member.role === 'head_coach' ? 'Head Coach' : member.role}
                        </span>
                      </div>
                      <p className="text-sm text-[#A1A1AA] truncate">{member.email}</p>
                      {!isActive && (
                        <p className="text-xs text-[#EF4444] mt-1 font-semibold uppercase tracking-wide">
                          Payment overdue - Access blocked
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-bold uppercase tracking-wide ${isActive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                      {isActive ? 'Paid' : 'Unpaid'}
                    </span>
                    <Switch
                      data-testid={`membership-toggle-${member.id}`}
                      checked={isActive}
                      onCheckedChange={() => toggleMembership(member.id, isActive)}
                      disabled={isUpdating}
                      className="data-[state=checked]:bg-[#22C55E] data-[state=unchecked]:bg-[#EF4444]"
                    />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
